import { useState, useContext } from 'react';
import { Button, Box, Typography, CircularProgress, Grid, TextField, Alert, RadioGroup, Radio } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

import { NAME_REGEX, DESCRIPTION_REGEX } from '../../utils/constants';
import { REQUIRED_FIELD, NAME_EXCEPTION, DESCRIPTION_EXCEPTION, INTERNAL_SERVER_ERROR } from '../../utils/exception';
import axiosInstance from '../../api';
import ColorPicker from '../../components/color-utils/ColorPicker';
import SERVER_API_URL from '../../config';
import { authContext } from '../../routes';
import StyledCardNoHeight from '../../components/card/StyledCardNoHeight';

export default function CategoryRegistrationForm() {
  const { user } = useContext(authContext);
  const [confessor, setConfessor] = useState(null);

  const getRandomColor = () => {
    const letters = '0123456789ABCDEF';
    let randomColor = '#';
    for (let i = 0; i < 6; i += 1) {
      randomColor += letters[Math.floor(Math.random() * 16)];
    }
    return randomColor;
  };

  const [color, setColor] = useState(getRandomColor());
  const [disabled, setDisabled] = useState(false);

  const handleSubmitNewCategory = async (event) => {
    event.preventDefault();
    setFormErrorSeverity('error');
    setDisabled(true);

    if (!formErrorOnConfessor && !formErrorOnCategoryName && !formErrorOnDescription) {
      const { categoryName, description } = event.target.elements;
      try {
        const response = await axiosInstance.post(`${SERVER_API_URL}/category/registerOne/${user && user.id}`, {
          categoryName: categoryName.value,
          description: description.value,
          color: color.toString(),
          type: confessor,
        });

        if (response.status === 201) {
          setFormError('ምድብ በተሳካ ሁኔታ ተመዝግቧል።');
          setFormErrorSeverity('success');
          setColor(getRandomColor());
        }
      } catch (e) {
        if (e.response.status === 406) {
          setFormError('የምድብ ስም አስቀድሞ ተወስዷል!');
        } else if (e.response.status === 500) {
          setFormError(INTERNAL_SERVER_ERROR);
        }
      }
    }else{
          setFormError('ሁሉንም ቦታዎች በአግባቡ ይሙሉ።');
    }
    setDisabled(false);
  };

  const [formError, setFormError] = useState(null);
  const [formErrorSeverity, setFormErrorSeverity] = useState('error');

  const [categoryNameClicked, setCategoryNameClicked] = useState(false);
  const [formErrorOnCategoryName, setFormErrorOnCategoryName] = useState(false);
  const [helperTextOnCategoryName, setHelperTextOnCategoryName] = useState(null);

  const [descriptionClicked, setDescriptionClicked] = useState(false);
  const [formErrorOnDescription, setFormErrorOnDescription] = useState(false);
  const [helperTextOnDescription, setHelperTextOnDescription] = useState(null);

  const [confessorClicked, setConfessorClicked] = useState(false);
  const [formErrorOnConfessor, setFormErrorOnConfessor] = useState(false);
  const [helperTextOnConfessor, setHelperTextOnConfessor] = useState(null);

  const handleFormValueBlur = (e) => {
    e.preventDefault();
    setFormError(null);
    setFormErrorSeverity('error');

    const { name, value } = e.target;
    switch (name) {
      case 'categoryName':
        if (value.length === 0) {
          setCategoryNameClicked(false);
          setFormErrorOnCategoryName(categoryNameClicked && value.length === 0);
          setHelperTextOnCategoryName(REQUIRED_FIELD);
        } else if (!NAME_REGEX.test(value)) {
          setFormErrorOnCategoryName(true);
          setHelperTextOnCategoryName(NAME_EXCEPTION);
        } else {
          setHelperTextOnCategoryName(null);
          setFormErrorOnCategoryName(false);
        }
        break;
      case 'description':
        if (value.length === 0) {
          setDescriptionClicked(false);
          setFormErrorOnDescription(descriptionClicked && value.length === 0);
          setHelperTextOnDescription(REQUIRED_FIELD);
        } else if (!DESCRIPTION_REGEX.test(value)) {
          setFormErrorOnDescription(true);
          setHelperTextOnDescription(DESCRIPTION_EXCEPTION);
        } else {
          setHelperTextOnDescription(null);
          setFormErrorOnDescription(false);
        }
        break;
      case 'confessor':
        if (value.length === 0) {
          setConfessorClicked(false);
          setFormErrorOnConfessor(confessorClicked && value.length === 0);
          setHelperTextOnConfessor(REQUIRED_FIELD);
        } else {
          setHelperTextOnConfessor(null);
          setFormErrorOnConfessor(false);
        }
        break;
      default:
        break;
    }
  };

  const handleFormValueChange = (e) => {
    e.preventDefault();
    setFormError(null);
    setFormErrorSeverity('error');
    const { name, value } = e.target;
    switch (name) {
      case 'categoryName':
        if (NAME_REGEX.test(value)) {
          setFormErrorOnCategoryName(false);
          setHelperTextOnCategoryName(null);
        } else {
          setFormErrorOnCategoryName(false);
          setHelperTextOnCategoryName(null);
        }
        break;
      case 'description':
        if (DESCRIPTION_REGEX.test(value)) {
          setFormErrorOnDescription(false);
          setHelperTextOnDescription(null);
        } else {
          setFormErrorOnDescription(false);
          setHelperTextOnDescription(null);
        }
        break;
      case 'confessor':
        setConfessor(value);
        if (DESCRIPTION_REGEX.test(value)) {
          setFormErrorOnConfessor(false);
          setHelperTextOnConfessor(null);
        } else {
          setFormErrorOnConfessor(false);
          setHelperTextOnConfessor(null);
        }
        break;
      default:
        break;
    }
  };

  const handleClearForm = () => {
    setFormError(null);
    setFormErrorSeverity('error');
    setConfessor(null);

    setCategoryNameClicked(false);
    setFormErrorOnCategoryName(false);
    setHelperTextOnCategoryName(null);

    setDescriptionClicked(false);
    setFormErrorOnDescription(false);
    setHelperTextOnDescription(null);
  };

  return (
    <StyledCardNoHeight
      sx={{ padding: { xs: 1, sm: 10 } }}
      component="form"
      autoComplete="off"
      onSubmit={handleSubmitNewCategory}
    >
      <Typography variant="h6" gutterBottom sx={{ color: 'text.secondary' }}>
        አዲስ ምድብ ጨምር
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={12}>
          {formError && (
            <Alert sx={{ width: '100%' }} severity={formErrorSeverity}>
              {formError}
            </Alert>
          )}
        </Grid>
        <Grid item xs={12} sm={9}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                error={formErrorOnCategoryName}
                id="categoryName"
                name="categoryName"
                label="የምድብ ስም"
                fullWidth
                // variant="standard"
                onChange={handleFormValueChange}
                helperText={formErrorOnCategoryName ? helperTextOnCategoryName : null}
                onFocus={() => setCategoryNameClicked(true)}
                onBlur={handleFormValueBlur}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <RadioGroup
                required
                error={formErrorOnConfessor}
                name="confessor"
                // variant="standard"
                onChange={handleFormValueChange}
                helperText={formErrorOnConfessor ? helperTextOnConfessor : null}
                onFocus={() => setConfessorClicked(true)}
                onBlur={handleFormValueBlur}
                value={confessor}
              >
                <Box sx={{ display: 'flex', mt: 1 }}>
                  <Typography color='text.secondary' sx={{ mr: 1 }}>የንስሃ አባት መሆን: </Typography>
                  <Box sx={{ display: 'flex' }}>
                    <Radio defaultChecked={confessor === "confessor"} value="confessor" sx={{ mt: -1 }} /> ይችላል
                    <Radio defaultChecked={confessor === "nonConfessor"} value="nonConfessor" sx={{ mt: -1 }} /> አይችልም
                  </Box>
                </Box>
              </RadioGroup>
            </Grid>
            <Grid item xs={12} sm={12}>
              <TextField
                required
                error={formErrorOnDescription}
                id="description"
                name="description"
                label="የምድብ መግለጫ"
                fullWidth
                multiline
                rows={5}
                onChange={handleFormValueChange}
                helperText={formErrorOnDescription ? helperTextOnDescription : null}
                onFocus={() => setDescriptionClicked(true)}
                onBlur={handleFormValueBlur}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sm={3}>
          <ColorPicker color={color} setColor={setColor} />
        </Grid>
        <Grid item xs={12} sm={12} sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button color="error" type="reset" onClick={handleClearForm} sx={{ minWidth: { xs: 80, sm: 150 } }}>
            <RefreshIcon /> ቅጽ ዳግም አስጀምር
          </Button>
          <Button
            disabled={disabled}
            variant="contained"
            type="submit"
            sx={{ marginLeft: { xs: 2, sm: 5 }, minWidth: { xs: 80, sm: 150 } }}
          >
            {disabled && <CircularProgress style={{ width: '20px', height: '20px' }} />} ቅጽ አስገባ
          </Button>
        </Grid>
      </Grid>
    </StyledCardNoHeight>
  );
}
